import { ErrorOutline } from '@mui/icons-material'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Typography,
} from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'
import { FC, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getPxAsRem } from 'shared/utils'
import { tryHandleCommand } from './commandHandlers'
import { CommandsHelp } from './CommandsHelp'
import { useGlobalSearch } from './hooks/useGlobalSearch'
import { useRecentlyVisited } from './hooks/useRecentlyVisited'
import { useRecentSearches } from './hooks/useRecentSearches'
import {
    createNavigationState,
    navigate as navFn,
    navigationStateToSelection,
} from './navigation'
import { RecentlyVisited } from './RecentlyVisited'
import { RecentSearches } from './RecentSearches'
import { ResultColumn } from './ResultColumn'
import { SearchInput, SearchInputHandle } from './SearchInput'

interface Selection {
    col: number
    row: number
}

interface GlobalSearchModalProps {
    isOpen: boolean
    onClose: () => void
    anchorEl: HTMLElement | null
}

const scrollableColumnSx: SxProps<Theme> = {
    borderRadius: getPxAsRem(8),
    height: getPxAsRem(350),
    overflowY: 'auto',
    padding: getPxAsRem(8, 0),
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,0.15)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'rgba(0,0,0,0.25)',
    },
}

const COMMAND_HINTS = [
    '@go research',
    '@go searches',
    '@go companies',
    '@logout',
    '@help',
]

export const GlobalSearchModal: FC<GlobalSearchModalProps> = ({
    isOpen,
    onClose,
}) => {
    const navigate = useNavigate()
    const inputRef = useRef<SearchInputHandle>(null)
    const [inputValue, setInputValue] = useState('')
    const [submittedQuery, setSubmittedQuery] = useState('')
    const [selection, setSelection] = useState<Selection | null>(null)
    const [showHelp, setShowHelp] = useState(false)

    const { recentSearches, addSearch, removeSearch, clearAll } =
        useRecentSearches()
    const { recentlyVisited } = useRecentlyVisited()
    const { results, isLoading, error, retry } = useGlobalSearch(submittedQuery)

    const isSearchMode = submittedQuery !== ''

    const getColumnLengths = (): number[] => {
        if (isSearchMode) {
            return [
                results.candidates.length,
                results.rosters.length,
                results.companies.length,
            ]
        }
        return [recentSearches.length, recentlyVisited.length]
    }

    const doNavigate = (direction: 'up' | 'down' | 'left' | 'right') => {
        const currentState = createNavigationState(
            getColumnLengths(),
            selection
        )
        const newState = navFn(currentState, direction)
        setSelection(navigationStateToSelection(newState))
    }

    const handleInputChange = (value: string) => {
        setInputValue(value)
        setSelection(null)
        if (!value.trim()) {
            setSubmittedQuery('')
        }
    }

    const handleSubmit = (e: React.KeyboardEvent | React.MouseEvent) => {
        const openInNewTab = e.metaKey || e.ctrlKey

        if (selection) {
            if (isSearchMode) {
                const items = [
                    results.candidates,
                    results.rosters,
                    results.companies,
                ][selection.col]
                const item = items[selection.row]
                if (item) {
                    if (openInNewTab) {
                        window.open(item.path, '_blank')
                    } else {
                        navigate(item.path)
                        handleClose()
                    }
                }
            } else {
                if (selection.col === 0) {
                    const search = recentSearches[selection.row]
                    if (search) {
                        setInputValue(search.query)
                        addSearch(search.query)
                        setSubmittedQuery(search.query)
                        setSelection(null)
                    }
                } else {
                    const item = recentlyVisited[selection.row]
                    if (item) {
                        if (openInNewTab) {
                            window.open(item.path, '_blank')
                        } else {
                            navigate(item.path)
                            handleClose()
                        }
                    }
                }
            }
            return
        }

        const trimmed = inputValue.trim()
        if (!trimmed) return

        if (trimmed.toLowerCase() === '@help') {
            setShowHelp(true)
            setInputValue('')
            return
        }

        if (tryHandleCommand(trimmed, navigate)) {
            handleClose()
            return
        }

        addSearch(trimmed)
        setSubmittedQuery(trimmed)
        setSelection(null)
    }

    const handleSelectRecentSearch = (query: string) => {
        setInputValue(query)
        addSearch(query)
        setSubmittedQuery(query)
        setSelection(null)
    }

    const handleResultSelect = () => {
        handleClose()
    }

    const handleClose = () => {
        setInputValue('')
        setSubmittedQuery('')
        setSelection(null)
        onClose()
    }

    const hasRecentSearches = recentSearches.length > 0
    const hasRecentlyVisited = recentlyVisited.length > 0
    const showEmptyState =
        !isSearchMode && !hasRecentSearches && !hasRecentlyVisited

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={handleClose}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: getPxAsRem(1100),
                        maxWidth: '95vw',
                        maxHeight: '80vh',
                    },
                }}
                onClick={() => inputRef.current?.focus()}
            >
                <Box padding={getPxAsRem(16, 16, 12)}>
                    <SearchInput
                        ref={inputRef}
                        value={inputValue}
                        onChange={handleInputChange}
                        onSubmit={handleSubmit}
                        onArrowUp={() => doNavigate('up')}
                        onArrowDown={() => doNavigate('down')}
                        onArrowLeft={() => doNavigate('left')}
                        onArrowRight={() => doNavigate('right')}
                        hasSelection={selection !== null}
                        onInputFocus={() => setSelection(null)}
                    />
                </Box>

                <Divider />

                <Box sx={{ padding: getPxAsRem(16) }}>
                    {showEmptyState ? (
                        <CommandsHelp />
                    ) : isSearchMode ? (
                        isLoading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: getPxAsRem(350),
                                    gap: getPxAsRem(16),
                                }}
                            >
                                <CircularProgress size={24} />
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setSubmittedQuery('')}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        ) : error ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: getPxAsRem(350),
                                    gap: getPxAsRem(16),
                                }}
                            >
                                <ErrorOutline
                                    sx={{ fontSize: 48, color: 'error.main' }}
                                />
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Search failed
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={retry}
                                >
                                    Try again
                                </Button>
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Box sx={scrollableColumnSx}>
                                        <ResultColumn
                                            title="Candidates"
                                            type="candidate"
                                            items={results.candidates}
                                            onSelect={handleResultSelect}
                                            highlightedRow={
                                                selection?.col === 0
                                                    ? selection.row
                                                    : undefined
                                            }
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box sx={scrollableColumnSx}>
                                        <ResultColumn
                                            title="Searches"
                                            type="roster"
                                            items={results.rosters}
                                            onSelect={handleResultSelect}
                                            highlightedRow={
                                                selection?.col === 1
                                                    ? selection.row
                                                    : undefined
                                            }
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box sx={scrollableColumnSx}>
                                        <ResultColumn
                                            title="Companies"
                                            type="company"
                                            items={results.companies}
                                            onSelect={handleResultSelect}
                                            highlightedRow={
                                                selection?.col === 2
                                                    ? selection.row
                                                    : undefined
                                            }
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        )
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Box sx={scrollableColumnSx}>
                                    <RecentSearches
                                        searches={recentSearches}
                                        onSelect={handleSelectRecentSearch}
                                        onRemove={removeSearch}
                                        onClearAll={clearAll}
                                        highlightedRow={
                                            selection?.col === 0
                                                ? selection.row
                                                : undefined
                                        }
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={scrollableColumnSx}>
                                    <RecentlyVisited
                                        items={recentlyVisited}
                                        onSelect={handleResultSelect}
                                        highlightedRow={
                                            selection?.col === 1
                                                ? selection.row
                                                : undefined
                                        }
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    {!showEmptyState && (
                        <Box
                            sx={{
                                marginTop: getPxAsRem(12),
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: getPxAsRem(8),
                            }}
                        >
                            {COMMAND_HINTS.map((cmd) => (
                                <Typography
                                    key={cmd}
                                    component="code"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        backgroundColor: 'other.background',
                                        color: 'text.secondary',
                                        padding: getPxAsRem(4, 8),
                                        borderRadius: getPxAsRem(4),
                                    }}
                                >
                                    {cmd}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Box>
            </Dialog>

            <Dialog open={showHelp} onClose={() => setShowHelp(false)}>
                <DialogTitle>Search Commands</DialogTitle>
                <DialogContent>
                    <CommandsHelp />
                </DialogContent>
            </Dialog>
        </>
    )
}
